<template>
  <div class="sale-group">
    <v-menu
      ref="menu"
      v-model="open"
      offset-y
      :left="menuLeft"
      nudge-bottom="4"
      min-width="0"
      max-width="none"
      eager
      :close-on-content-click="false"
      content-class="app-menu app-menu--picker"
      role="none"
    >
      <template v-slot:activator="{ on, attrs }">
        <app-button
          kind="field"
          icon="layers"
          chevron
          class="sale-group__btn"
          :class="{ 'app-btn--fixed': !!buttonStyle }"
          :style="buttonStyle"
          :title="state.title"
          :aria-label="state.name"
          aria-haspopup="dialog"
          v-bind="attrs"
          v-on="on"
        >{{ state.text }}</app-button>
      </template>
      <div
        ref="panel"
        class="sale-group__panel"
        :class="panelClasses"
        :style="panelStyle"
        role="dialog"
        aria-label="Gruppierung"
        tabindex="-1"
        @keydown.tab="trapTab"
        @keydown.esc="onEsc"
      >
        <div v-scroll-hint class="sale-group__body">
          <div class="sale-group__block">
            <span :id="levelsId" class="app-label sale-group__label">Gruppieren nach</span>
            <ol v-if="value.length" class="sale-group__levels" :aria-labelledby="levelsId">
              <!-- nach Position geschlüsselt: Ein anderes Feld ändert die Zeile, statt sie neu anzulegen (der Fokus bleibt) -->
              <li v-for="(key, i) in value" :key="i" class="sale-group__level" :data-level="key">
                <span class="sale-group__nr" aria-hidden="true">{{ i + 1 }}</span>
                <app-select
                  class="sale-group__select"
                  block
                  :prefix="'Ebene ' + (i + 1)"
                  hide-prefix
                  :value="key"
                  :items="itemsFor(key)"
                  @input="setLevel(i, $event)"
                />
                <app-icon-button
                  icon="arrowUp"
                  :label="'Ebene ' + (i + 1) + ' nach oben'"
                  :boxed="false"
                  :disabled="i === 0"
                  data-action="up"
                  @click="move(i, -1)"
                />
                <app-icon-button
                  icon="arrowDown"
                  :label="'Ebene ' + (i + 1) + ' nach unten'"
                  :boxed="false"
                  :disabled="i === value.length - 1"
                  data-action="down"
                  @click="move(i, 1)"
                />
                <app-icon-button
                  icon="x"
                  :label="'Ebene ' + (i + 1) + ' entfernen'"
                  :boxed="false"
                  data-action="remove"
                  @click="remove(i)"
                />
              </li>
            </ol>
            <!-- nächste Ebene als leeres Feld: die Wahl eines Felds fügt sie hinzu -->
            <div v-if="value.length < maxLevels" class="sale-group__level sale-group__level--neu">
              <span class="sale-group__nr" aria-hidden="true">{{ value.length + 1 }}</span>
              <app-select
                ref="add"
                class="sale-group__select sale-group__add"
                block
                icon="plus"
                placeholder="Ebene hinzufügen"
                :items="addItems"
                :disabled="!addItems.length"
                @input="add"
              />
            </div>
            <p v-else class="sale-group__hint">Höchstens {{ maxLevels }} Ebenen</p>
          </div>

          <div class="sale-group__block sale-group__block--vorlagen">
            <span :id="presetsId" class="app-label sale-group__label">Vorlagen</span>
            <div class="sale-group__presets" role="group" :aria-labelledby="presetsId">
              <app-button
                v-for="preset in presets"
                :key="preset.value"
                class="sale-group__preset"
                :pressed="isPreset(preset)"
                @click="choosePreset(preset)"
              >{{ preset.text }}</app-button>
            </div>
          </div>
        </div>

        <div class="sale-group__foot">
          <app-button :disabled="!value.length" @click="reset">Keine Gruppierung</app-button>
          <!-- „Übernehmen“ schließt das Panel (Kundenwunsch 14.10.2026), die Wahl wirkt wie bisher sofort -->
          <app-button @click="apply">Übernehmen</app-button>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script>
// Gruppierung der Verkäufe (Kundenwunsch 12.10.2026: „grupierung zudem frei wählbar machen nicht nur die vorgegebenen
// also auch eine art menü zur auswahl der gruppierung“; Felder und Vorlagen in saleGrouping.js). Feldknopf mit Ebenen
// und dem Wert („Fahrer › Datum“, ohne Gruppierung nur „Gruppierung“ wie „Filter“), zugänglicher Name „Gruppierung: Fahrer,
// Datum“ bzw. „Gruppierung: keine“, Tooltip „Gruppierung: Fahrer › Datum“. Panel wie das Filter-Panel (v-menu mit
// role="none", Panel role="dialog" „Gruppierung“, rechtsbündig zum Knopf mit alignRight; passt es dort nicht ins
// Fenster, linksbündig):
// - „Gruppieren nach“: bis zu drei Ebenen als nummerierte Zeilen, je eine Auswahl (AppSelect) und die randlosen
//   Icon-Knöpfe „nach oben“, „nach unten“ und „entfernen“; darunter die nächste Ebene als leeres Feld „Ebene
//   hinzufügen“ (die Wahl eines Felds fügt sie hinzu). Angeboten werden nur Felder mit mindestens zwei Werten in den
//   geladenen Zeilen (fields, die Seite berechnet sie beim Öffnen), keins doppelt; das Feld einer Ebene bleibt immer
//   wählbar.
// - „Vorlagen“: die bisherigen festen Gruppierungen als Umschaltknöpfe (gedrückt, wenn die Ebenen gleich sind).
// - Fuß: „Keine Gruppierung“ setzt zurück.
// Jede Änderung wirkt sofort (input mit den neuen Ebenen), das Panel bleibt offen. Ereignisse: input, open (die Seite
// berechnet dann fields neu), close.
// Tastatur: Fokus beim Öffnen auf das erste Bedienelement, Tab bleibt im Panel, Esc schließt das Panel und gibt den
// Fokus an den Feldknopf; ist das Menü einer Auswahl offen, schließt Esc nur dieses. Nach der Wahl eines Felds (Maus
// oder Tastatur) bleibt der Fokus an der Auswahl bzw. an „Ebene hinzufügen“, mit der dritten Ebene an der neuen Ebene;
// nach Verschieben und Entfernen bleibt er in der Zeile bzw. geht zur nächsten (seit der Prüfung vom 12.10.2026 fiel er
// sonst auf die Seite, Esc schloss das Panel dann nicht mehr). Das Panel enthält keine v-list-item (die Einträge der
// Auswahlen liegen in eigenen Menüs), sonst übernähme VMenu die Pfeiltasten.
// Maße: Maus 360 breit, Touch 400, Handy Fensterbreite − 24 (samt Kante); Höhe wie das Filter-Panel (panelMenu.js),
// mit Scrollschatten. Ändert sich die Zahl der Ebenen, werden Höhe und Lage neu berechnet.
import { raiseMenu } from '@/components/ui/AppSelect.vue';
import panelMenu, { alignRightFor } from './panelMenu';
import { GROUP_PRESETS, MAX_LEVELS, describeGrouping, levelItems, sameLevels } from './saleGrouping';

export default {
  name: 'SaleGroupMenu',
  mixins: [panelMenu],
  props: {
    // Ebenen, z. B. ['fahrer', 'tag']
    value: { type: Array, required: true },
    // Feldschlüssel mit mindestens zwei Werten (availableGroupFields aus saleGrouping.js)
    fields: { type: Array, default: () => [] },
    minWidth: { type: Number, default: null },
    alignRight: { type: Boolean, default: false }
  },
  data() {
    return {
      presets: GROUP_PRESETS,
      maxLevels: MAX_LEVELS
    };
  },
  computed: {
    state() {
      return describeGrouping(this.value);
    },
    buttonStyle() {
      return this.minWidth ? { minWidth: this.minWidth + 'px' } : null;
    },
    addItems() {
      return levelItems(this.value, this.fields, null);
    },
    panelClasses() {
      return {
        'sale-group__panel--touch': this.touch,
        'sale-group__panel--handy': this.handy
      };
    },
    levelsId() {
      return 'sale-group-' + this._uid + '-ebenen';
    },
    presetsId() {
      return 'sale-group-' + this._uid + '-vorlagen';
    }
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.place();
        this.measure();
        this.$emit('open');
        this.$nextTick(() => {
          this.updateMenu();
          this.focusWhenShown(12);
        });
      } else {
        this.$emit('close');
      }
    },
    // mehr oder weniger Ebenen ändern die Höhe des offenen Panels (und die Seite blendet Auf-/Zuklappen ein oder aus):
    // Höhe und Lage neu berechnen
    'value.length'() {
      this.$nextTick(this.relayout);
    }
  },
  mounted() {
    raiseMenu(this.$refs.menu);
  },
  methods: {
    // Seite am Knopf beim Öffnen (panelMenu.alignRightFor)
    place() {
      const viewport = document.documentElement.clientWidth || window.innerWidth;
      const width = (this.handy ? viewport - 26 : this.touch ? 400 : 360) + 2;
      this.rightAligned = alignRightFor(this.activator(), width, !!this.alignRight);
    },
    itemsFor(key) {
      return levelItems(this.value, this.fields, key);
    },
    isPreset(preset) {
      return sameLevels(preset.levels, this.value);
    },
    emitLevels(levels) {
      if (!sameLevels(levels, this.value)) {
        this.$emit('input', levels);
      }
    },
    // Fokus bleibt an der Auswahl der Ebene (die Zeile bleibt dank des Positionsschlüssels erhalten)
    setLevel(index, key) {
      const levels = this.value.slice();
      levels[index] = key;
      this.emitLevels(levels);
      this.focusLater(() => this.focusInLevel(index, 'select'));
    },
    // Fokus an „Ebene hinzufügen“; mit der dritten Ebene verschwindet es, dann auf die neue Ebene
    add(key) {
      this.emitLevels(this.value.concat([key]));
      this.focusLater(() => (this.$refs.add ? this.focusAdd() : this.focusInLevel(this.value.length - 1, 'select')));
    },
    // Übernehmen: Panel schließen, Fokus zurück an den Gruppierungs-Knopf
    apply() {
      this.open = false;
      this.$nextTick(() => {
        const button = this.activator();
        if (button && button.focus) {
          button.focus();
        }
      });
    },
    focusAdd() {
      const button = this.$refs.add && this.$refs.add.$el ? this.$refs.add.$el.querySelector('.app-btn') : null;
      if (!button || button.disabled) {
        return false;
      }
      button.focus();
      return true;
    },
    move(index, delta) {
      const target = index + delta;
      if (target < 0 || target >= this.value.length) {
        return;
      }
      const levels = this.value.slice();
      const key = levels[index];
      levels[index] = levels[target];
      levels[target] = key;
      this.emitLevels(levels);
      // Fokus bleibt am verschobenen Feld: derselbe Knopf, am Rand der andere
      this.$nextTick(() => {
        const first = delta < 0 ? 'up' : 'down';
        if (!this.focusInLevel(target, first)) {
          this.focusInLevel(target, first === 'up' ? 'down' : 'up');
        }
      });
    },
    remove(index) {
      this.emitLevels(this.value.filter((key, i) => i !== index));
      this.$nextTick(() => {
        if (!this.focusInLevel(index, 'remove') && !this.focusInLevel(index - 1, 'remove')) {
          this.focusFirst();
        }
      });
    },
    choosePreset(preset) {
      this.emitLevels(preset.levels.slice());
    },
    reset() {
      this.emitLevels([]);
      // „Keine Gruppierung“ wird gesperrt und verlöre den Fokus: zurück an den Anfang des Panels
      this.$nextTick(this.focusFirst);
    },
    // Bedienelement einer Ebenenzeile fokussieren: 'select' (Auswahl), 'up', 'down', 'remove'; false, wenn es fehlt
    // oder gesperrt ist
    focusInLevel(index, what) {
      const panel = this.$refs.panel;
      const rows = panel ? panel.querySelectorAll('.sale-group__levels > .sale-group__level') : [];
      const row = rows[index];
      if (!row) {
        return false;
      }
      const el = row.querySelector(what === 'select' ? '.app-select .app-btn' : '[data-action="' + what + '"]');
      if (!el || el.disabled) {
        return false;
      }
      el.focus();
      return true;
    },
    // Esc bei offenem Menü einer Auswahl: nur dieses schließt (VMenu der Auswahl), das Panel bleibt offen
    onEsc(e) {
      const target = e.target;
      if (target && target.getAttribute('aria-expanded') === 'true' && target.closest('.app-select')) {
        e.stopPropagation();
      }
    }
  }
};
</script>

<style scoped>
.sale-group {
  display: inline-flex;
  flex: none;
  max-width: 100%;
  min-width: 0;
  vertical-align: middle;
}
/* lange Ebenenfolgen kürzen („Fahrer › Linie › Tarifzonen“); der volle Wert steht im Tooltip und im Namen */
.sale-group__btn {
  max-width: 320px;
}
html.dichte-touch .sale-group__btn {
  max-width: 340px;
}
.sale-group__panel {
  display: flex;
  flex-direction: column;
  width: 360px;
  /* mit der Kante des Menüs (2 × 1) genau 12 Abstand zu beiden Fensterrändern */
  max-width: calc(100vw - 26px);
  color: var(--ink);
  font-family: var(--font);
}
.sale-group__panel--touch {
  width: 400px;
}
.sale-group__panel--handy {
  width: calc(100vw - 26px);
}
/* Fokus am Panel selbst nur nach einem Klick auf eine freie Stelle (Esc und Tab wirken weiter): ohne Rahmen */
.sale-group__panel:focus {
  outline: none;
}
.sale-group__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 8px 0;
}
/* Scrollschatten wie im Filter-Panel (v-scroll-hint) */
.sale-group__body::before,
.sale-group__body::after {
  content: '';
  position: sticky;
  z-index: 2;
  display: block;
  height: 8px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
}
.sale-group__body::before {
  top: 0;
  margin-bottom: -8px;
  background: radial-gradient(farthest-side at 50% 0, var(--sh-scroll), transparent);
}
.sale-group__body::after {
  bottom: 0;
  margin-top: -8px;
  background: radial-gradient(farthest-side at 50% 100%, var(--sh-scroll), transparent);
}
.sale-group__body.scroll--oben::before,
.sale-group__body.scroll--unten::after {
  opacity: 1;
}
/* seitlich 12 wie die übrigen Menüs der Leiste */
.sale-group__block {
  padding: 8px 12px;
}
.sale-group__block--vorlagen {
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--line-2);
}
.sale-group__label {
  margin-bottom: 8px;
}

/* ---------------------------------------------------------------- Ebenen */
.sale-group__levels {
  margin: 0;
  padding: 0;
  list-style: none;
}
.sale-group__level {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  padding: 0;
}
.sale-group__level + .sale-group__level,
.sale-group__levels + .sale-group__level {
  margin-top: 8px;
}
.sale-group__nr {
  flex: none;
  width: 16px;
  margin-right: 4px;
  font-size: var(--fs-small);
  line-height: var(--lh-small);
  font-weight: 600;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
  text-align: center;
}
.sale-group__select {
  flex: 1 1 auto;
  min-width: 0;
}
/* neue Ebene so breit wie die Auswahl einer Ebene (dort folgen drei Icon-Knöpfe mit je 4 Abstand; davor Nummer 16,
   Rand 4 und Abstand 4) */
.sale-group__level--neu .sale-group__select {
  flex: 0 1 auto;
  width: calc(100% - 24px - 3 * (var(--h-ib) + 4px));
}
/* Handy: dafür ist die Zeile zu schmal („Ebene hinzufügen“ würde gekürzt), die neue Ebene nutzt die ganze Zeile */
.sale-group__panel--handy .sale-group__level--neu .sale-group__select {
  flex: 1 1 auto;
  width: auto;
}
.sale-group__hint {
  margin: 8px 0 0 20px;
  font-size: var(--fs-small);
  line-height: var(--lh-small);
  color: var(--ink-3);
}

/* ---------------------------------------------------------------- Vorlagen */
.sale-group__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* ---------------------------------------------------------------- Fuß */
.sale-group__foot {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px;
  border-top: 1px solid var(--line-2);
}
</style>
